import React, { useId } from 'react';

export default function HeroSection({
  image,
  title,
  subtitle,
  subtitleBold = true,
  contentAlign = 'center',
  fullHeight = true,
  alt,
  captionTag,
  children,
}) {
  // Prefer WebP via CSS image-set(); older browsers fall back to the original via a regular
  // `background-image: url(...)` declaration that is overridden in the same block by the
  // image-set() declaration which they silently drop.
  const webp = image.replace(/\.(jpe?g|png)$/i, '.webp');
  const id = useId().replace(/:/g, '');
  const cls = `hero-${id}`;
  const css = `.${cls}{background-image:url("${image}");background-image:image-set(url("${webp}") type("image/webp"),url("${image}") type("image/jpeg"));}`;

  // `contentAlign` decides where the title/subtitle/CTA sits vertically inside
  // the hero. Default 'center' preserves existing behaviour on every other page.
  // Pass 'top' on the Home page so the copy doesn't cover members' faces in the
  // group photo.
  const alignClass =
    contentAlign === 'top'    ? 'justify-start pt-28 sm:pt-32'
  : contentAlign === 'bottom' ? 'justify-end pb-16 sm:pb-24'
  :                             'justify-center';

  // The hero banner is a CSS background (so parallax/overlays work cleanly). We
  // mark the section as role="img" with an aria-label so screen-reader users
  // still hear what the picture shows, and we drop a 1px fetchpriority="high"
  // img into the DOM to give browsers an explicit LCP candidate for the WebP
  // asset — same URL the background will request, so no extra bytes.
  const accessibleLabel = alt || title || '';

  return (
    <section
      data-testid="hero-section"
      role="img"
      aria-label={accessibleLabel || undefined}
      className={`${fullHeight ? 'hero-parallax' : 'hero-parallax-short'} ${cls}`}
    >
      <style>{css}</style>
      <img
        src={webp}
        alt=""
        aria-hidden="true"
        fetchpriority="high"
        loading="eager"
        decoding="async"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      <div className="hero-overlay" />
      <div className={`relative z-10 flex flex-col items-center ${alignClass} h-full min-h-[inherit] text-center px-4`}>
        {title && (
          <h1
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-none text-white mb-4 max-w-4xl"
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <p
            data-testid="hero-subtitle"
            className={`text-base sm:text-lg max-w-2xl leading-relaxed ${
              subtitleBold
                ? 'text-white font-bold [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]'
                : 'text-white/80'
            }`}
          >
            {subtitle}
          </p>
        )}
        {children}
      </div>
      {captionTag && (
        <div
          data-testid="hero-caption-tag"
          className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 z-10 bg-[#0A0A0A]/70 backdrop-blur-sm text-white text-[11px] sm:text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 border-l-2 border-[#F0A500]"
        >
          {captionTag}
        </div>
      )}
    </section>
  );
}
